import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import TransportBanner from './TransportBanner';

const getTimeOfDay = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
};

const timeSlideVariants = {
  morning: {
    image: '/hero-images/hero-slide-3-morning.jpg',
    title: 'Start Your Day with Support and Strength',
    subtitle: 'Every morning is a fresh opportunity to invest in your mental wellness and build resilience',
    alt: 'Calm sunrise over the ocean symbolizing hope and renewal',
    tint: 'from-amber-900/30 via-orange-800/15 to-yellow-700/10',
    accent: 'rgba(251,191,36,0.5)'
  },
  afternoon: {
    image: '/hero-images/hero-slide-3-afternoon.jpg',
    title: 'Empowering You to Thrive Every Step of the Way',
    subtitle: 'Stay grounded and focused with compassionate care that meets you where you are',
    alt: 'Bright natural sunlight over landscape symbolizing growth and momentum',
    tint: 'from-emerald-900/25 via-teal-800/10 to-sky-700/10',
    accent: 'rgba(52,211,153,0.5)'
  },
  evening: {
    image: '/hero-images/hero-slide-3.jpg',
    title: 'Find Peace, Support, and Renewal This Evening',
    subtitle: 'Reflect on your progress and restore your spirit with dedicated professional support',
    alt: 'Warm sunset over ocean symbolizing reflection and peace',
    tint: 'from-rose-900/30 via-orange-900/15 to-amber-800/10',
    accent: 'rgba(244,114,182,0.5)'
  },
  night: {
    image: '/hero-images/hero-slide-3-night.jpg',
    title: 'Support and Care When You Need It Most',
    subtitle: 'You are not alone — compassionate help is always within reach, day or night',
    alt: 'Peaceful night sky by a calm lake symbolizing comfort and reassurance',
    tint: 'from-indigo-900/40 via-blue-900/20 to-slate-900/15',
    accent: 'rgba(129,140,248,0.5)'
  }
};

const HeroSlider = ({ onBookAppointment, onTakeSelfAssessment }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [timeOfDay, setTimeOfDay] = useState(getTimeOfDay);
  const [prevTimeOfDay, setPrevTimeOfDay] = useState(null);
  const [timeTransition, setTimeTransition] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const slideTimerRef = useRef(null);
  const sliderRef = useRef(null);

  // Re-check time every 5 minutes with crossfade
  useEffect(() => {
    const timer = setInterval(() => {
      const newTime = getTimeOfDay();
      if (newTime !== timeOfDay) {
        setPrevTimeOfDay(timeOfDay);
        setTimeTransition(true);
        setTimeOfDay(newTime);
        // Clear transition after animation completes
        setTimeout(() => {
          setPrevTimeOfDay(null);
          setTimeTransition(false);
        }, 1200);
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [timeOfDay]);

  const timeSlide = timeSlideVariants[timeOfDay] || timeSlideVariants.afternoon;
  const prevTimeSlide = prevTimeOfDay ? timeSlideVariants[prevTimeOfDay] : null;

  // Parallax scroll tracking
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (sliderRef.current) {
            const rect = sliderRef.current.getBoundingClientRect();
            const sliderHeight = rect.height;
            // Only apply parallax when slider is in view
            if (rect.bottom > 0 && rect.top < window.innerHeight) {
              setScrollY(window.scrollY * 0.35);
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const slides = [
    {
      id: 1,
      image: '/hero-images/hero-slide-1.jpg',
      title: 'Your Journey to Mental Wellness Starts Here',
      subtitle: 'Compassionate, evidence-based behavioral health care for individuals and families',
      alt: 'Multi-generational family gathering showing warmth and connection',
      isTimeAdaptive: false
    },
    {
      id: 2,
      image: '/hero-images/hero-slide-2.jpg',
      title: 'Professional Care, Personal Connection',
      subtitle: 'One-on-one therapy with licensed clinicians in a safe, supportive environment',
      alt: 'Therapist in a calm office having a supportive conversation with a client',
      isTimeAdaptive: false
    },
    {
      id: 3,
      image: timeSlide.image,
      title: timeSlide.title,
      subtitle: timeSlide.subtitle,
      alt: timeSlide.alt,
      isTimeAdaptive: true,
      tint: timeSlide.tint,
      accent: timeSlide.accent
    }
  ];

  useEffect(() => {
    slideTimerRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 7000);
    return () => clearInterval(slideTimerRef.current);
  }, [slides.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  const goToSlide = (index) => setCurrentSlide(index);

  return (
    <div ref={sliderRef} className="relative w-full overflow-hidden bg-gray-900" data-testid="hero-slider">
      {/* Single semantic H1 for SEO — visually hidden, always in DOM */}
      <h1 className="sr-only">Mental Health &amp; Behavioral Health Services in Maryland</h1>

      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0 absolute inset-0'
          }`}
          aria-hidden={index !== currentSlide}
        >
          <div className="relative w-full overflow-hidden" style={{ maxHeight: '75vh' }}>
            {/* Time-adaptive slide: crossfade between old and new images */}
            {slide.isTimeAdaptive && prevTimeSlide && timeTransition && (
              <img
                src={prevTimeSlide.image}
                alt=""
                className="w-full h-full object-cover block absolute inset-0 transition-opacity duration-[1200ms] opacity-0"
                style={{ transform: `translateY(${scrollY}px) scale(1.1)`, willChange: 'transform' }}
                aria-hidden="true"
              />
            )}
            <img
              src={slide.image}
              alt={slide.alt}
              className={`w-full h-full object-cover block ${
                slide.isTimeAdaptive && timeTransition ? 'animate-[fadeIn_1.2s_ease-in-out]' : ''
              }`}
              style={{ transform: `translateY(${scrollY}px) scale(1.1)`, willChange: 'transform', minHeight: '50vh' }}
              data-testid="hero-image"
            />

            {/* Standard gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/20"></div>

            {/* Time-adaptive color tint overlay */}
            {slide.isTimeAdaptive && (
              <div
                className={`absolute inset-0 bg-gradient-to-br ${slide.tint} transition-all duration-[1200ms] ease-in-out`}
              />
            )}

            {/* Content overlay */}
            <div className="absolute inset-0 flex items-start pt-[12%]">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                {/* Desktop */}
                <div className="hidden md:flex md:items-center md:justify-between">
                  <div className="flex-1 max-w-2xl">
                    <h2
                      className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 ${
                        index === currentSlide ? 'animate-[slideUp_0.8s_ease-out_both]' : ''
                      }`}
                      style={slide.isTimeAdaptive ? {
                        textShadow: `0 0 40px ${slide.accent}, 0 2px 8px rgba(0,0,0,0.5)`
                      } : undefined}
                    >
                      {slide.title}
                    </h2>
                    <p
                      className={`text-lg md:text-xl text-white/90 mb-8 ${
                        index === currentSlide ? 'animate-[slideUp_0.8s_ease-out_0.15s_both]' : ''
                      }`}
                    >
                      {slide.subtitle}
                    </p>
                  </div>
                  <div className="flex flex-col gap-6 items-center" style={{ marginLeft: 'auto', paddingRight: '5%' }}>
                    <button
                      onClick={onBookAppointment}
                      className={`w-full min-w-[300px] bg-transparent backdrop-blur-sm text-white px-8 py-4 rounded-lg hover:bg-blue-600/80 transition-all text-lg font-semibold border-2 border-white/30 shadow-[0_0_20px_rgba(37,99,235,0.6)] hover:shadow-[0_0_40px_rgba(37,99,235,1)] ${
                        index === currentSlide ? 'animate-[slideUp_0.6s_ease-out_0.3s_both]' : ''
                      }`}
                      data-testid="slider-book-appointment-btn"
                    >
                      Schedule Your Appointment
                    </button>
                    <button
                      onClick={onTakeSelfAssessment}
                      className={`w-full min-w-[300px] bg-transparent backdrop-blur-sm text-white px-8 py-4 rounded-lg hover:bg-purple-600/80 transition-all text-lg font-semibold border-2 border-white/30 shadow-[0_0_20px_rgba(147,51,234,0.6)] hover:shadow-[0_0_40px_rgba(147,51,234,1)] ${
                        index === currentSlide ? 'animate-[slideUp_0.6s_ease-out_0.45s_both]' : ''
                      }`}
                      data-testid="slider-self-assessment-btn"
                    >
                      Take Self-Assessment
                    </button>
                    <a
                      href="/services/comprehensive-mental-health"
                      className={`w-full min-w-[300px] bg-transparent backdrop-blur-sm text-white border-2 border-white/50 px-8 py-4 rounded-lg hover:bg-white/80 hover:text-blue-600 transition-all text-lg font-semibold text-center shadow-[0_0_20px_rgba(255,255,255,0.5)] hover:shadow-[0_0_40px_rgba(255,255,255,1)] ${
                        index === currentSlide ? 'animate-[slideUp_0.6s_ease-out_0.6s_both]' : ''
                      }`}
                    >
                      Learn More
                    </a>
                  </div>
                </div>

                {/* Mobile */}
                <div className="md:hidden flex flex-col items-center justify-center">
                  <div className="max-w-2xl mb-8 text-center">
                    <h2
                      className={`text-3xl sm:text-4xl font-bold text-white leading-tight mb-6 ${
                        index === currentSlide ? 'animate-[slideUp_0.8s_ease-out_both]' : ''
                      }`}
                      style={slide.isTimeAdaptive ? {
                        textShadow: `0 0 40px ${slide.accent}, 0 2px 8px rgba(0,0,0,0.5)`
                      } : undefined}
                    >
                      {slide.title}
                    </h2>
                    <p
                      className={`text-lg text-white/90 mb-8 ${
                        index === currentSlide ? 'animate-[slideUp_0.8s_ease-out_0.15s_both]' : ''
                      }`}
                    >
                      {slide.subtitle}
                    </p>
                  </div>
                  <div className="flex flex-col gap-6 w-full max-w-md px-4">
                    <button
                      onClick={onBookAppointment}
                      className={`w-full bg-transparent backdrop-blur-sm text-white px-6 py-4 rounded-lg hover:bg-blue-600/80 transition-all text-lg font-semibold border-2 border-white/30 shadow-[0_0_20px_rgba(37,99,235,0.6)] hover:shadow-[0_0_40px_rgba(37,99,235,1)] ${
                        index === currentSlide ? 'animate-[slideUp_0.6s_ease-out_0.3s_both]' : ''
                      }`}
                      data-testid="slider-book-appointment-btn"
                    >
                      Schedule Your Appointment
                    </button>
                    <button
                      onClick={onTakeSelfAssessment}
                      className={`w-full bg-transparent backdrop-blur-sm text-white px-6 py-4 rounded-lg hover:bg-purple-600/80 transition-all text-lg font-semibold border-2 border-white/30 shadow-[0_0_20px_rgba(147,51,234,0.6)] hover:shadow-[0_0_40px_rgba(147,51,234,1)] ${
                        index === currentSlide ? 'animate-[slideUp_0.6s_ease-out_0.45s_both]' : ''
                      }`}
                      data-testid="slider-self-assessment-btn"
                    >
                      Take Self-Assessment
                    </button>
                    <a
                      href="/services/comprehensive-mental-health"
                      className={`w-full bg-transparent backdrop-blur-sm text-white border-2 border-white/50 px-6 py-4 rounded-lg hover:bg-white/80 hover:text-blue-600 transition-all text-lg font-semibold text-center shadow-[0_0_20px_rgba(255,255,255,0.5)] hover:shadow-[0_0_40px_rgba(255,255,255,1)] ${
                        index === currentSlide ? 'animate-[slideUp_0.6s_ease-out_0.6s_both]' : ''
                      }`}
                    >
                      Learn More
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white p-3 rounded-full transition-all z-10"
            data-testid="slider-prev-btn"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white p-3 rounded-full transition-all z-10"
            data-testid="slider-next-btn"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Slide Indicators */}
      {slides.length > 1 && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide ? 'bg-white w-8' : 'bg-white/50'
              }`}
              data-testid={`slider-indicator-${index}`}
            />
          ))}
        </div>
      )}

      {/* Transportation Banner — animated ticker at bottom of hero */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <TransportBanner />
      </div>
    </div>
  );
};

export default HeroSlider;
